import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';

import Button from '@/components/Button';
import {
  AppVersionChannel,
  AppVersionPin,
  createAppVersionPin,
  updateAppVersionPin,
} from '@/services/appVersionPinsAPI';

type Props = {
  open: boolean;
  pin: AppVersionPin | null;
  onClose: () => void;
  onSaved: () => void;
};

const CHANNELS: AppVersionChannel[] = ['production', 'preview'];

// Parse newline/comma-separated text into a clean array of update ids.
const parseUpdateIds = (raw: string): string[] =>
  raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const AppVersionPinModal = ({ open, pin, onClose, onSaved }: Props) => {
  const isEdit = !!pin;
  const [channel, setChannel] = useState<AppVersionChannel>('production');
  const [allowedUpdateIdsText, setAllowedUpdateIdsText] = useState('');
  const [maxAllowedCount, setMaxAllowedCount] = useState<number>(3);
  const [gateEnabled, setGateEnabled] = useState<boolean>(true);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setChannel(pin?.channel ?? 'production');
      setAllowedUpdateIdsText((pin?.allowed_update_ids ?? []).join('\n'));
      setMaxAllowedCount(pin?.max_allowed_count ?? 3);
      setGateEnabled(pin?.gate_enabled ?? true);
      setNotes(pin?.notes ?? '');
      setError(null);
    }
  }, [open, pin]);

  const handleSubmit = async () => {
    const ids = parseUpdateIds(allowedUpdateIdsText);

    setLoading(true);
    setError(null);

    try {
      if (isEdit && pin) {
        await updateAppVersionPin(pin.id, {
          allowed_update_ids: ids,
          max_allowed_count: maxAllowedCount,
          gate_enabled: gateEnabled,
          notes: notes.trim() || null,
        });
      } else {
        await createAppVersionPin({
          channel,
          allowed_update_ids: ids,
          max_allowed_count: maxAllowedCount,
          gate_enabled: gateEnabled,
          notes: notes.trim() || null,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to save pin',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Pin' : 'Create Pin'}</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        {error && <Alert severity="error">{error}</Alert>}

        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch
              checked={gateEnabled}
              onChange={(e) => setGateEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              <strong>{gateEnabled ? 'Gate ON' : 'Gate OFF'}</strong> —{' '}
              {gateEnabled
                ? 'devices not on an allowed update id are locked'
                : 'release flow: gate is OFF, all devices skip enforcement'}
            </Typography>
          }
        />

        <FormControl fullWidth size="small" disabled={isEdit}>
          <InputLabel>Channel</InputLabel>
          <Select
            value={channel}
            label="Channel"
            onChange={(e) => setChannel(e.target.value as AppVersionChannel)}
            MenuProps={{ disablePortal: true }}
          >
            {CHANNELS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Allowed Update IDs (newest first, one per line)"
          placeholder={'0fa2c3b8-5a8b-4d1c-9b91-...\nprev-update-id'}
          value={allowedUpdateIdsText}
          onChange={(e) => setAllowedUpdateIdsText(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={4}
          helperText="Devices on any of these updateIds skip the lock screen. The 1-min Expo sync prepends new ids automatically — you only need to edit when overriding."
        />

        <TextField
          label="Max allowed versions"
          type="number"
          value={maxAllowedCount}
          onChange={(e) => setMaxAllowedCount(Number(e.target.value) || 1)}
          inputProps={{ min: 1, max: 20 }}
          fullWidth
          size="small"
          helperText="Sync will keep at most this many ids in the list (oldest dropped first)."
        />

        <TextField
          label="Notes (optional)"
          placeholder="e.g. release 5.07 — fixes hold-orphan bug"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button
          a11yLabel="Cancel"
          label="Cancel"
          appearance="plain"
          onClick={onClose}
        />
        <Button
          a11yLabel={isEdit ? 'Save pin' : 'Create pin'}
          label={isEdit ? 'Save' : 'Create'}
          isLoading={loading}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default AppVersionPinModal;
